/**
 * ============================================================================
 * USER SEARCH PAGE - Feature Enhancement
 * ============================================================================
 * 
 * Search for users by name, phone, or email
 * Backend endpoint: GET /api/v1/users/search
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../hooks/useUser';
import { useToast } from '../providers';
import { useI18n } from '../providers';
import { UserDashboardLayout } from '../components/user';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import Input from '../components/common/Input';
import Spinner from '../components/common/Spinner';
import Avatar from '../components/common/Avatar';
import StatusBadge from '../components/common/StatusBadge';
import EmptyState from '../components/common/EmptyState';
import '../styles/animations.css';
import '../styles/global.css';

/**
 * User Search Page Component
 * @returns {JSX.Element}
 */
const UserSearch = () => {
  const navigate = useNavigate();
  const { searchUsers, loading } = useUser();
  const { showError } = useToast();
  const { language } = useI18n();
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      return;
    }

    try {
      setIsSearching(true);
      setHasSearched(true);
      const results = await searchUsers(searchQuery.trim());
      const usersData = Array.isArray(results) ? results : results?.data || results?.users || [];
      setUsers(usersData);
    } catch (error) {
      showError(
        error.message ||
          (language === 'fr' ? 'Échec de la recherche' : 'Search failed')
      );
      console.error('Search error:', error);
      setUsers([]);
    } finally {
      setIsSearching(false);
    }
  };

  const getVerificationBadge = (level) => {
    if (level === 2) {
      return { variant: 'success', text: language === 'fr' ? 'Vérifié' : 'Verified' };
    }
    if (level === 1) {
      return { variant: 'warning', text: language === 'fr' ? 'En Attente' : 'Pending' };
    }
    return { variant: 'default', text: language === 'fr' ? 'Non Vérifié' : 'Unverified' };
  };

  return (
    <UserDashboardLayout>
      {/* Header */}
      <div className="mb-8 animate-fadeInUp">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard')}
          className="mb-4"
        >
          ← {language === 'fr' ? 'Retour au tableau de bord' : 'Back to Dashboard'}
        </Button>
        <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
          🔍 {language === 'fr' ? 'Recherche d\'Utilisateurs' : 'User Search'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          {language === 'fr'
            ? 'Recherchez des utilisateurs par nom, téléphone ou email'
            : 'Search for users by name, phone, or email'}
        </p>
      </div>

      {/* Search Form */}
      <Card
        variant="glass"
        className="backdrop-blur-xl border-white/20 dark:border-white/10 mb-6 animate-fadeInUp"
        style={{ animationDelay: '0.1s' }}
        hover
      >
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={
                language === 'fr'
                  ? 'Rechercher par nom, téléphone ou email...'
                  : 'Search by name, phone, or email...'
              }
              className="w-full"
            />
          </div>
          <Button
            type="submit"
            variant="gradient"
            loading={isSearching}
            disabled={isSearching || !searchQuery.trim()}
            className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600"
          >
            {language === 'fr' ? 'Rechercher' : 'Search'}
          </Button>
        </form>
      </Card>

      {/* Search Results */}
      {hasSearched && (
        <div className="animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
          {loading || isSearching ? (
            <Card variant="glass" className="p-12">
              <div className="flex items-center justify-center">
                <Spinner size="lg" />
              </div>
            </Card>
          ) : users.length === 0 ? (
            <Card variant="glass" className="p-12">
              <EmptyState
                icon="🔍"
                title={
                  language === 'fr'
                    ? 'Aucun utilisateur trouvé'
                    : 'No users found'
                }
                description={
                  language === 'fr'
                    ? 'Essayez avec un autre terme de recherche'
                    : 'Try a different search term'
                }
              />
            </Card>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'fr'
                    ? `${users.length} utilisateur(s) trouvé(s)`
                    : `${users.length} user(s) found`}
                </p>
              </div>
              {users.map((user, index) => {
                const verification = getVerificationBadge(user.verificationLevel || user.verification_level || 0);
                return (
                  <Card
                    key={user.id || index}
                    variant="glass"
                    className="backdrop-blur-xl border-white/20 dark:border-white/10 p-4 hover:scale-[1.02] transition-all cursor-pointer animate-fadeInUp"
                    style={{ animationDelay: `${0.1 + index * 0.05}s` }}
                    hover
                    clickable
                    onClick={() => navigate(`/users/${user.id}`)}
                  >
                    <div className="flex items-center gap-4">
                      <Avatar
                        src={user.profilePhotoUrl || user.profile_photo_url}
                        name={user.fullName || user.full_name || 'User'}
                        size="lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-black text-gray-900 dark:text-white">
                            {user.fullName || user.full_name || language === 'fr' ? 'Utilisateur' : 'User'}
                          </h3>
                          <StatusBadge variant={verification.variant} size="sm">
                            {verification.text}
                          </StatusBadge>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                          {user.phone && (
                            <span>📱 {user.phone}</span>
                          )}
                          {user.email && (
                            <span>📧 {user.email}</span>
                          )}
                          {user.rating_average !== undefined && (
                            <span>
                              ⭐ {user.rating_average?.toFixed(1) || '0.0'} ({user.rating_count || 0})
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/users/${user.id}`);
                        }}
                      >
                        {language === 'fr' ? 'Voir Profil' : 'View Profile'} →
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Privacy Note */}
      {hasSearched && users.length > 0 && (
        <Card
          variant="glass"
          className="backdrop-blur-xl border-white/20 dark:border-white/10 mt-6 p-4 bg-gradient-to-br from-blue-50/80 to-green-50/80 dark:from-blue-900/20 dark:to-green-900/20 animate-fadeInUp"
          hover
        >
          <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
            {language === 'fr'
              ? '🔒 Seules les informations publiques sont affichées. Le respect de la vie privée est notre priorité.'
              : '🔒 Only public information is displayed. Privacy is our priority.'}
          </p>
        </Card>
      )}
    </UserDashboardLayout>
  );
};

export default UserSearch;
